import { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { MinusIcon, PlusIcon } from '@heroicons/react/24/outline'
import { useTranslation } from 'react-i18next'
import { makeRequest } from 'services/request.service'
import { showAlert } from 'services/alerts.service'
import { deriveTunnelWindows, plantsToWindows } from 'utils/blockWindows'
// import { BeakerIcon, BugAntIcon, CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/16/solid'

import 'styles/TunnelComponent.css'

const getMatchingRows = (rows, tunnelNumber, rowIndex) => {
  const suffix = `T${tunnelNumber}-R${rowIndex + 1}`
  const matches = rows.filter(row => row.rowId?.endsWith(suffix))

  return {
    subA: matches.find(row => row.rowId.includes('SUB_A')),
    subB: matches.find(row => row.rowId.includes('SUB_B')),
    soil: matches.find(row => !row.rowId.includes('SUB_'))
  }
}

const getWindowLabel = (i, leftCount) => {
  if (i === leftCount) return 'A'
  return `W${i < leftCount ? leftCount - i : i - leftCount}`
}

const AccessWindowContent = ({ subARow, subBRow, soilRow }) => {
  const { t } = useTranslation()
  return (
    <>
      {subARow && (
        <div className='text-[10px] text-green-700'>SUB_A: {subARow.numberOfPlants}</div>
      )}
      A
      {subBRow && (
        <div className='text-[10px] text-blue-700'>SUB_B: {subBRow.numberOfPlants}</div>
      )}
      {soilRow && (
        <div className='text-[10px] text-gray-700'>{t('mgmt.tunnel.plants')} {soilRow.numberOfPlants}</div>
      )}
    </>
  )
}

const renderWindowCell = ({
  rowIndex,
  windowIndex,
  isAccessWindow,
  subARow,
  subBRow,
  soilRow,
  windowLabel,
  status,
  isDone,
  handleDragOver,
  handleDrop
}) => {
  const statusClass = isAccessWindow ? 'access' : status
  const doneClass = isDone && !isAccessWindow ? ' window-done' : ''

  return (
    <div
      key={`window-${rowIndex}-${windowIndex}`}
      className={`window relative ${statusClass} level-number${doneClass}`}
      onDragOver={handleDragOver}
      onDrop={(e) => handleDrop(e, rowIndex, windowIndex)}
    >
      <div className='text-sm font-medium text-center leading-tight'>
        {isAccessWindow
          ? <AccessWindowContent subARow={subARow} subBRow={subBRow} soilRow={soilRow} />
          : windowLabel}
      </div>
    </div>
  )
}

const TunnelComponent = ({
  tunnelNumber,
  configType = 'default',
  numberOfWindows = 16,
  isHydroponicBlock,
  accessWindow,
  toggleIsTunnelRowModalOpen,
  farmingBlockId,
  doneRowIds,
  onExposeRefresh,
  onWindowCountChange,
  onDataChanged
}) => {
  const { t } = useTranslation()
  const hasFetched = useRef(false)
  const [windowStates, setWindowStates] = useState({})
  const [tunnelRowsByBlock, setTunnelRowData] = useState([])
  const [addingLine, setAddingLine] = useState(false)

  // Line count is data-driven: the default (8/9/6) unless the tunnel already has
  // more lines in block_rows (e.g. after manually adding one), so the new line shows.
  const defaultRows = isHydroponicBlock ? 6 : (tunnelNumber === 1 ? 8 : 9)
  const maxRowInData = Math.max(0, ...tunnelRowsByBlock.map(row => {
    const m = /-T(\d+)-R(\d+)\s*$/.exec(row.rowId || '')
    return m && Number(m[1]) === tunnelNumber ? Number(m[2]) : 0
  }))
  const numberOfRows = Math.max(defaultRows, maxRowInData)

  // Tunnel-wide SUB_A / SUB_B window counts (the deepest line per side). Used
  // ONLY as a fallback for empty lines that have no plant data yet — each real
  // line derives its own exact window count below.
  const tunnelWin = deriveTunnelWindows(tunnelRowsByBlock, isHydroponicBlock).byTunnel.get(tunnelNumber)
  const useDerivedSplit = !!tunnelWin && (tunnelWin.subA > 0 || tunnelWin.subB > 0)

  // Per-line window layout, derived from THIS line's own plant counts using the
  // same rule as the farming-block map (soil = ceil, hydroponic = round). Each
  // line shows its exact windows instead of the tunnel-wide maximum, so a line
  // with fewer plants renders fewer windows. Returns the access position and the
  // total (SUB_A windows + access + SUB_B windows).
  const getRowLayout = (subARow, subBRow, soilRow) => {
    if (subARow || subBRow) {
      const subA = plantsToWindows(subARow?.numberOfPlants, isHydroponicBlock)
      const subB = plantsToWindows(subBRow?.numberOfPlants, isHydroponicBlock)
      return { accessIndex: subA, totalWindows: subA + 1 + subB }
    }
    if (soilRow) {
      const soil = plantsToWindows(soilRow?.numberOfPlants, isHydroponicBlock)
      if (soil > 0) {
        // No sub-block split: keep the configured walkway slot, clamped inside
        // the derived window count, and add 1 for the access window itself.
        return { accessIndex: Math.min(Math.max(accessWindow - 1, 0), soil), totalWindows: soil + 1 }
      }
    }
    // Empty line (no data yet): fall back to the tunnel/block-level defaults.
    return useDerivedSplit
      ? { accessIndex: tunnelWin.subA, totalWindows: tunnelWin.subA + 1 + tunnelWin.subB }
      : { accessIndex: accessWindow - 1, totalWindows: numberOfWindows }
  }

  // const handleDragStart = (e, status) => {
  //   e.dataTransfer.setData('status', status)
  // }

  const handleDragOver = (e) => {
    e.preventDefault()
  }

  const handleDrop = (e, rowIndex, windowIndex) => {
    if (accessWindow === windowIndex + 1) return
    e.preventDefault()
    const status = e.dataTransfer.getData('status')
    setWindowStates(prev => ({
      ...prev,
      [`${rowIndex}-${windowIndex}`]: status
    }))
  }

  // const handleReset = () => {
  //   setWindowStates({})
  // }

  const onRowHeaderClick = (tunnelNumber, rowIndex) => {
    const rowSuffix = `T${tunnelNumber}-R${rowIndex + 1}`
    const matchingRows = tunnelRowsByBlock.filter(row =>
      row.rowId?.endsWith(rowSuffix)
    )

    const subARow = matchingRows.find(row => row.rowId.includes('SUB_A'))
    const subBRow = matchingRows.find(row => row.rowId.includes('SUB_B'))
    const soilRow = matchingRows.find(row => !row.rowId.includes('SUB_'))

    let initialData

    if (subARow || subBRow) {
      initialData = {
        rowId: subARow?.rowId || subBRow?.rowId,
        subA: {
          cropId: subARow?.cropId || '',
          varietyId: subARow?.varietyId || '',
          numberOfPlants: subARow?.numberOfPlants || '',
          rowId: subARow?.rowId || '',
          barcode: subARow?.barcode ?? null
        },
        subB: {
          cropId: subBRow?.cropId || '',
          varietyId: subBRow?.varietyId || '',
          numberOfPlants: subBRow?.numberOfPlants || '',
          rowId: subBRow?.rowId || '',
          barcode: subBRow?.barcode ?? null
        }
      }
    } else if (soilRow) {
      initialData = {
        cropId: soilRow.cropId || '',
        varietyId: soilRow.varietyId || '',
        numberOfPlants: soilRow.numberOfPlants || '',
        rowId: soilRow.rowId || '',
        barcode: soilRow.barcode ?? null
      }
    } else {
      initialData = {}
    }

    toggleIsTunnelRowModalOpen(rowIndex, initialData)
  }

  const fetchTunnelRowsByBlockId = async (farmingBlockId) => {
    const response = await makeRequest({
      method: 'GET',
      url: `/management/farming-blocks/${farmingBlockId}/rows`,
      headers: {
        Authorization: `Bearer ${localStorage.getItem('secret')}`
      }
    })

    setTunnelRowData(Array.isArray(response?.data) ? response.data : [])
  }

  useEffect(() => {
    if (!farmingBlockId) return

    if (hasFetched.current) return
    hasFetched.current = true

    fetchTunnelRowsByBlockId(farmingBlockId)
  }, [farmingBlockId])

  useEffect(() => {
    if (typeof onExposeRefresh === 'function') {
      onExposeRefresh(() => fetchTunnelRowsByBlockId(farmingBlockId))
    }
  }, [farmingBlockId, onExposeRefresh])

  // Add a new line to this tunnel (backend copies the current last line).
  const handleAddLine = async () => {
    if (addingLine || !farmingBlockId) return
    try {
      setAddingLine(true)
      await makeRequest({
        method: 'POST',
        url: `/management/farming-blocks/${farmingBlockId}/tunnels/${tunnelNumber}/rows`,
        // Send an explicit empty JSON body so axios sets Content-Type:
        // application/json; without a body it defaults to
        // application/x-www-form-urlencoded, which Fastify rejects with 415.
        data: {},
        headers: {
          Authorization: `Bearer ${localStorage.getItem('secret')}`,
          'Content-Type': 'application/json'
        }
      })
      await fetchTunnelRowsByBlockId(farmingBlockId)
      if (typeof onDataChanged === 'function') onDataChanged()
      showAlert({ text: t('mgmt.tunnel.lineAdded'), icon: 'success' })
    } catch (error) {
      console.error('Error adding line:', error)
      showAlert({ text: t('mgmt.tunnel.lineAddFailed'), icon: 'error' })
    } finally {
      setAddingLine(false)
    }
  }

  return (
    <div className={`tunnel ${configType}`}>
      <div className='tunnel-header'>
        {typeof onWindowCountChange === 'function' && (
          <div className='tunnel-grid-controls' aria-label={t('mgmt.tunnel.gridControlsAria', { n: tunnelNumber })}>
            <span className='grid-control-label'>{t('mgmt.tunnel.windows')}</span>
            <button
              type='button'
              aria-label={t('mgmt.tunnel.removeWindowAria', { n: tunnelNumber })}
              title={t('mgmt.tunnel.removeWindow')}
              disabled={numberOfWindows <= accessWindow}
              onClick={() => onWindowCountChange(numberOfWindows - 1)}
              className='grid-control-button'
            >
              <MinusIcon className='w-4 h-4' />
            </button>
            <span className='grid-control-value'>{numberOfWindows}</span>
            <button
              type='button'
              aria-label={t('mgmt.tunnel.addWindowAria', { n: tunnelNumber })}
              title={t('mgmt.tunnel.addWindow')}
              onClick={() => onWindowCountChange(numberOfWindows + 1)}
              className='grid-control-button add'
            >
              <PlusIcon className='w-4 h-4' />
            </button>
          </div>
        )}
        <button
          type='button'
          className='tunnel-add-line-button'
          title={t('mgmt.tunnel.addLineTitle')}
          disabled={addingLine}
          onClick={handleAddLine}
        >
          <PlusIcon className='w-4 h-4' />
          <span>{addingLine ? t('mgmt.tunnel.adding') : t('mgmt.tunnel.addLine')}</span>
        </button>
      </div>

      {/* <div className='status-markers'>
        <div className='markers-container'>
          <div
            className='status-marker healthy'
            draggable
            onDragStart={(e) => handleDragStart(e, 'healthy')}
          >
            <CheckCircleIcon style={{ width: 20, height: 20 }} /> Healthy
          </div>
          <div
            className='status-marker disease'
            draggable
            onDragStart={(e) => handleDragStart(e, 'disease')}
          >
            <ExclamationTriangleIcon style={{ width: 20, height: 20 }} /> Disease
          </div>
          <div
            className='status-marker nutrient-deficiency'
            draggable
            onDragStart={(e) => handleDragStart(e, 'nutrient-deficiency')}
          >
            <BeakerIcon style={{ width: 20, height: 20 }} /> Nutrient Deficiency
          </div>
          <div
            className='status-marker plague'
            draggable
            onDragStart={(e) => handleDragStart(e, 'plague')}
          >
            <BugAntIcon style={{ width: 20, height: 20 }} /> Plague
          </div>
        </div>
        <button
          className='reset-status-button'
          onClick={handleReset}
          title='Reset all windows'
        >
          Reset All
        </button>
      </div> */}

      <div className='tunnel-grid-container'>
        <div className='tunnel-grid'>
          <div className='level-numbers'>
            {Array.from({ length: numberOfRows }, (_, i) => (
              <div
                key={`level-${i}`}
                className='level-number'
                onClick={() => onRowHeaderClick(tunnelNumber, i)}
              >
                R{i + 1}
              </div>
            ))}
          </div>

          <div className='grid-lines'>
            {Array.from({ length: numberOfRows }, (_, rowIndex) => {
              const { subA: subARow, subB: subBRow, soil: soilRow } = getMatchingRows(
                tunnelRowsByBlock,
                tunnelNumber,
                rowIndex
              )

              const { accessIndex, totalWindows } = getRowLayout(subARow, subBRow, soilRow)
              const allIndexes = Array.from({ length: totalWindows }, (_, i) => i)

              const left = allIndexes.slice(0, accessIndex).reverse()
              const right = allIndexes.slice(accessIndex + 1)
              const orderedIndexes = [...left, accessIndex, ...right]

              // Make every line a full, aligned grid: pad each side up to the
              // tunnel's deepest SUB_A / SUB_B with visible gray "no plants" cells.
              // Padding SUB_A (top) lines up all the "A" access cells on one row;
              // padding SUB_B (bottom) keeps the grid rectangular. Soil / fallback
              // lines have no SUB split (max 0), so both stay 0.
              const topPad = Math.max(0, (tunnelWin?.subA ?? 0) - left.length)
              const botPad = Math.max(0, (tunnelWin?.subB ?? 0) - right.length)
              const renderEmptyCell = (side, s) => (
                <div key={`empty-${side}-${rowIndex}-${s}`} className='window level-number window-empty' title={t('mgmt.tunnel.noPlants')} aria-hidden='true'>
                  <div className='text-sm font-medium text-center leading-tight'>&nbsp;</div>
                </div>
              )

              return (
                <div key={`row-${rowIndex}`} className='grid-row'>
                  {Array.from({ length: topPad }, (_, s) => renderEmptyCell('top', s))}
                  {orderedIndexes.map((windowIndex, i) => {
                    const isAccessWindow = windowIndex === accessIndex
                    const windowLabel = getWindowLabel(i, left.length)
                    const status = windowStates[`${rowIndex}-${windowIndex}`] || 'healthy'

                    // Owning line for this window: SUB_A above the access, SUB_B
                    // below, or the soil/full row when there is no split. A window
                    // is "done" when its line's row_id was scanned.
                    const owningRow = windowIndex < accessIndex ? (subARow || soilRow) : (subBRow || soilRow)
                    const isDone = !!(doneRowIds && owningRow?.rowId && doneRowIds.has(owningRow.rowId))

                    return renderWindowCell({
                      rowIndex,
                      windowIndex,
                      isAccessWindow,
                      subARow,
                      subBRow,
                      soilRow,
                      windowLabel,
                      status,
                      isDone,
                      handleDragOver,
                      handleDrop
                    })
                  })}
                  {Array.from({ length: botPad }, (_, s) => renderEmptyCell('bot', s))}
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}

TunnelComponent.propTypes = {
  tunnelNumber: PropTypes.number.isRequired,
  accessWindow: PropTypes.number.isRequired,
  farmingBlockId: PropTypes.number.isRequired,
  toggleIsTunnelRowModalOpen: PropTypes.func.isRequired,
  configType: PropTypes.oneOf(['default', 'custom']),
  numberOfWindows: PropTypes.number,
  isHydroponicBlock: PropTypes.bool,
  doneRowIds: PropTypes.instanceOf(Set),
  onExposeRefresh: PropTypes.func,
  onWindowCountChange: PropTypes.func,
  onDataChanged: PropTypes.func
}

export default TunnelComponent
